import { Component, createSignal, For, Show } from "solid-js";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { CsvDropzone, CsvMappingForm, MAPPABLE_FIELDS } from "../import";
import { buildCsvMapping, type PartialCsvMapping } from "../../utils/csv";
import { importCsv, suggestCsvMapping } from "../../api/workItems";
import type { CsvColumnMapping, ImportResult } from "../../api/workItems";

type Step = "upload" | "mapping" | "preview" | "importing" | "done";

export interface ImportCsvModalProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  onImportComplete: () => void;
}

export const ImportCsvModal: Component<ImportCsvModalProps> = (props) => {
  const [step, setStep] = createSignal<Step>("upload");
  const [file, setFile] = createSignal<File | null>(null);
  const [headers, setHeaders] = createSignal<string[]>([]);
  const [rows, setRows] = createSignal<string[][]>([]);
  const [mapping, setMapping] = createSignal<PartialCsvMapping>({});
  const [importResult, setImportResult] = createSignal<ImportResult | null>(
    null,
  );
  const [importError, setImportError] = createSignal<string | null>(null);
  const [suggestingMapping, setSuggestingMapping] = createSignal(false);

  const reset = () => {
    setStep("upload");
    setFile(null);
    setHeaders([]);
    setRows([]);
    setMapping({});
    setImportResult(null);
    setImportError(null);
    setSuggestingMapping(false);
  };

  const handleClose = () => {
    reset();
    props.onClose();
  };

  const handleFileParsed = (
    selected: File,
    csvHeaders: string[],
    csvRows: string[][],
  ) => {
    setFile(selected);
    setHeaders(csvHeaders);
    setRows(csvRows);
    setStep("mapping");

    // Ask AI to suggest column mappings in the background
    setSuggestingMapping(true);
    suggestCsvMapping(props.projectId, csvHeaders, csvRows.slice(0, 3))
      .then((suggested) => {
        // Only apply suggestions for fields that aren't already mapped
        setMapping((prev) => {
          const next = { ...prev };
          for (const { key } of MAPPABLE_FIELDS) {
            if (next[key] === undefined && suggested[key] !== undefined) {
              next[key] = suggested[key];
            }
          }
          return next;
        });
      })
      .catch(() => {
        // AI suggestion is best-effort — silently ignore errors
      })
      .finally(() => setSuggestingMapping(false));
  };

  const canProceedToPreview = () => mapping().title !== undefined;

  const previewRows = () => rows().slice(0, 5);

  const previewColumns = (): Array<{
    key: keyof CsvColumnMapping;
    label: string;
    colIndex: number;
  }> => {
    const m = mapping();
    const result: Array<{
      key: keyof CsvColumnMapping;
      label: string;
      colIndex: number;
    }> = [];
    for (const f of MAPPABLE_FIELDS) {
      const colIndex = m[f.key];
      if (colIndex !== undefined) {
        result.push({ key: f.key, label: f.label, colIndex });
      }
    }
    return result;
  };

  const handleImport = async () => {
    const csvMapping = buildCsvMapping(mapping());
    if (!csvMapping) return;

    setStep("importing");
    setImportError(null);
    try {
      const currentFile = file();
      if (!currentFile) return;
      const result = await importCsv(props.projectId, currentFile, csvMapping);
      setImportResult(result);
      setStep("done");
      props.onImportComplete();
    } catch (e) {
      setImportError(e instanceof Error ? e.message : "Import failed");
      setStep("preview");
    }
  };

  const stepTitle = () => {
    switch (step()) {
      case "upload":
        return "Import CSV — Upload";
      case "mapping":
        return "Import CSV — Map Columns";
      case "preview":
        return "Import CSV — Preview";
      case "importing":
        return "Import CSV — Importing...";
      case "done":
        return "Import CSV — Complete";
    }
  };

  return (
    <Modal
      open={props.open}
      onClose={handleClose}
      title={stepTitle()}
      size="lg"
    >
      {/* Upload step */}
      <Show when={step() === "upload"}>
        <div class="space-y-4">
          <p class="text-sm text-ink-2">
            Upload a CSV file to import work items. The first row must be a
            header row.
          </p>
          <CsvDropzone onFileParsed={handleFileParsed} />
        </div>
      </Show>

      {/* Mapping step */}
      <Show when={step() === "mapping"}>
        <div class="space-y-4">
          <p class="text-sm text-ink-2">
            Map your CSV columns to work item fields. Title is required.
          </p>
          <Show when={suggestingMapping()}>
            <div class="flex items-center gap-2 text-xs text-signal-magenta bg-signal-magenta-tint border border-signal-magenta/25 px-3 py-1.5 rounded">
              <svg
                class="w-3.5 h-3.5 animate-spin"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                />
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
              AI is suggesting column mappings...
            </div>
          </Show>
          <CsvMappingForm
            headers={headers()}
            mapping={mapping()}
            onChange={setMapping}
          />
          <div class="flex justify-between pt-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setStep("upload")}
            >
              Back
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={!canProceedToPreview()}
              onClick={() => setStep("preview")}
            >
              Preview
            </Button>
          </div>
        </div>
      </Show>

      {/* Preview step */}
      <Show when={step() === "preview"}>
        <div class="space-y-4">
          <p class="text-sm text-ink-2">
            Showing up to 5 rows of your data. Verify the mapping looks correct
            before importing.
          </p>
          <Show when={importError()}>
            <div class="px-3 py-2 bg-signal-red-tint border border-signal-red/25 rounded text-sm text-signal-red">
              {importError()}
            </div>
          </Show>
          <div class="overflow-x-auto rounded border border-line">
            <table class="w-full text-sm">
              <thead class="bg-sunken">
                <tr>
                  <For each={previewColumns()}>
                    {(col) => (
                      <th class="px-3 py-2 text-left font-medium text-ink-2 border-b border-line whitespace-nowrap">
                        {col.label}
                      </th>
                    )}
                  </For>
                </tr>
              </thead>
              <tbody>
                <For each={previewRows()}>
                  {(row) => (
                    <tr class="border-b border-line last:border-0 hover:bg-sunken">
                      <For each={previewColumns()}>
                        {(col) => (
                          <td class="px-3 py-2 text-ink-2 max-w-xs truncate">
                            {row[col.colIndex] ?? ""}
                          </td>
                        )}
                      </For>
                    </tr>
                  )}
                </For>
              </tbody>
            </table>
          </div>
          <p class="text-xs text-ink-3">
            {rows().length} total rows to import.
          </p>
          <div class="flex justify-between pt-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setStep("mapping")}
            >
              Back
            </Button>
            <Button variant="primary" size="sm" onClick={handleImport}>
              Import
            </Button>
          </div>
        </div>
      </Show>

      {/* Importing step */}
      <Show when={step() === "importing"}>
        <div class="flex flex-col items-center justify-center py-10 gap-3">
          <svg
            class="w-8 h-8 text-ink animate-spin"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            />
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
          <p class="text-sm text-ink-2">Importing items...</p>
        </div>
      </Show>

      {/* Done step */}
      <Show when={step() === "done"}>
        <div class="space-y-4">
          <div class="flex items-center gap-2 text-signal-green">
            <svg
              class="w-5 h-5 shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span class="text-sm font-medium">Import complete</span>
          </div>
          <Show when={importResult()}>
            {(result) => (
              <div class="space-y-2">
                <div class="flex gap-4 text-sm">
                  <span class="text-ink-2">
                    <span class="font-semibold text-signal-green">
                      {result().created}
                    </span>{" "}
                    created
                  </span>
                  <span class="text-ink-2">
                    <span class="font-semibold text-ink-3">
                      {result().skipped}
                    </span>{" "}
                    skipped
                  </span>
                </div>
                <Show when={result().errors.length > 0}>
                  <div class="rounded border border-signal-amber/25 bg-signal-amber-tint p-3 space-y-1">
                    <p class="text-xs font-medium text-signal-amber">
                      {result().errors.length} row error(s):
                    </p>
                    <For each={result().errors}>
                      {(err) => (
                        <p class="text-xs text-signal-amber">
                          Row {err.row}: {err.message}
                        </p>
                      )}
                    </For>
                  </div>
                </Show>
              </div>
            )}
          </Show>
          <div class="flex justify-end pt-2">
            <Button variant="primary" size="sm" onClick={handleClose}>
              Done
            </Button>
          </div>
        </div>
      </Show>
    </Modal>
  );
};
